import { memo, useMemo } from 'react';
import Svg, { G, Line, Path, Text } from 'react-native-svg';
import { fitText } from '../ComboChart/labels';
import { horizontalBarPath } from '../ComboChart/paths';
import { ComboBarStyle, ComboTheme } from '../ComboChart/types';
import {
  TwoSidedBarsSide,
  kTwoSidedBarThickness,
  twoSidedLayout,
} from './layout';

export interface TwoSidedBarsChartProps {
  width: number;
  /** Down the middle, one row each. */
  categories: string[];
  left: TwoSidedBarsSide;
  /** Absent: the right half stays empty (one side in the data). */
  right?: TwoSidedBarsSide;
  dataLabels: boolean;
  bar: ComboBarStyle;
  /** A tick's value as text. */
  format: (value: number) => string;
  /** A value short, when full labels would squeeze the bars. */
  compact?: (value: number) => string;
  theme: ComboTheme;
}

/**
 * A pyramid: per category, one bar to the left and one to the right of a
 * shared axis, the category's name between them, both sides on one scale.
 */
export const TwoSidedBarsChart = memo((props: TwoSidedBarsChartProps) => {
  const { width, categories, left, right, bar, theme } = props;
  const f = theme.fontSize;
  const layout = useMemo(
    () =>
      twoSidedLayout({
        width,
        categories,
        left,
        right,
        dataLabels: props.dataLabels,
        bar,
        format: props.format,
        compact: props.compact,
        fontSize: f,
      }),
    [
      width,
      categories,
      left,
      right,
      props.dataLabels,
      bar,
      props.format,
      props.compact,
      f,
    ],
  );
  const { band, top, half, scale } = layout;
  const plotHeight = band * categories.length;
  const h = kTwoSidedBarThickness;
  const sides = [
    { side: left, axis: layout.leftAxis, dir: -1, labels: layout.leftLabels },
    ...(right
      ? [
          {
            side: right,
            axis: layout.rightAxis,
            dir: 1,
            labels: layout.rightLabels,
          },
        ]
      : []),
  ];
  return (
    <Svg width={width} height={layout.height}>
      {sides.map(({ side, axis, dir, labels }) => (
        <G key={side.key}>
          <Text
            x={axis + (dir * half) / 2}
            y={f + 2}
            fontSize={f}
            fontFamily={theme.fontFamily}
            fontWeight="600"
            fill={side.color}
            textAnchor="middle"
          >
            {fitText(side.name, half, f)}
          </Text>
          <Line
            x1={axis}
            x2={axis}
            y1={top}
            y2={top + plotHeight}
            stroke={theme.axisLine}
          />
          {scale.ticks.map(tick => (
            <Text
              key={tick}
              x={axis + dir * scale.toY(tick)}
              y={top + plotHeight + f + 4}
              fontSize={f}
              fontFamily={theme.fontFamily}
              fill={theme.axisText}
              textAnchor={tick === 0 ? (dir < 0 ? 'end' : 'start') : 'middle'}
            >
              {props.format(tick)}
            </Text>
          ))}
          {categories.map((_, i) => {
            const value = side.values[i];
            if (value === undefined || !Number.isFinite(value)) return null;
            const y = top + band * i + (band - h) / 2;
            const end = axis + dir * scale.toY(Math.max(value, 0));
            const text = labels[i];
            return (
              <G key={i}>
                <Path
                  d={horizontalBarPath(axis, end, y, h, bar.cornerRadius)}
                  fill={side.color}
                />
                {props.dataLabels && text ? (
                  <Text
                    x={end + dir * 4}
                    y={y + h / 2 + f * 0.35}
                    fontSize={f}
                    fontFamily={theme.fontFamily}
                    fill={theme.label}
                    textAnchor={dir < 0 ? 'end' : 'start'}
                  >
                    {text}
                  </Text>
                ) : null}
              </G>
            );
          })}
        </G>
      ))}
      {layout.names.map((name, i) => (
        <Text
          key={i}
          x={width / 2}
          y={top + band * (i + 0.5) + f * 0.35}
          fontSize={f}
          fontFamily={theme.fontFamily}
          fill={theme.axisText}
          textAnchor="middle"
        >
          {name}
        </Text>
      ))}
    </Svg>
  );
});
